import { useMemo } from 'react';
import type { CatalogModel, CatalogVendor, CatalogChannelPricing } from '@nadoutong/contracts';
import { useT } from '../i18n/index.ts';

function fmtCNY(usd: number, mult: number, fx: number): string {
  return `¥${(usd * mult * fx).toFixed(2)}`;
}

function fmtDiscount(mult: number): string {
  const zhe = mult * 10;
  if (Number.isInteger(zhe)) return `${zhe}折`;
  return `${zhe.toFixed(1)}折`;
}

function vendorName(slug: string, vendors: readonly CatalogVendor[]): string {
  return vendors.find((v) => v.slug === slug)?.display_name ?? slug;
}

interface VendorGroup {
  readonly vendor: string;
  readonly channels: readonly CatalogChannelPricing[];
  readonly models: readonly CatalogModel[];
}

interface UnifiedData {
  readonly groups: VendorGroup[];
  readonly maxChannels: number;
}

function buildData(models: readonly CatalogModel[]): UnifiedData {
  const map = new Map<string, { models: CatalogModel[]; channelMap: Map<string, CatalogChannelPricing> }>();
  for (const m of models) {
    if (m.input_per_mtok_usd === null || m.channels.length === 0) continue;
    let entry = map.get(m.vendor);
    if (!entry) {
      entry = { models: [], channelMap: new Map() };
      map.set(m.vendor, entry);
    }
    entry.models.push(m);
    for (const ch of m.channels) {
      if (!entry.channelMap.has(ch.slug)) {
        entry.channelMap.set(ch.slug, ch);
      }
    }
  }
  const groups: VendorGroup[] = [];
  let maxChannels = 0;
  for (const [vendor, entry] of map) {
    const channels = Array.from(entry.channelMap.values()).sort((a, b) => a.rate_multiplier - b.rate_multiplier);
    groups.push({ vendor, channels, models: entry.models });
    if (channels.length > maxChannels) maxChannels = channels.length;
  }
  return { groups, maxChannels };
}

function ChannelCell({ model, channelSlug, fxRate, isInput, padClass }: {
  readonly model: CatalogModel;
  readonly channelSlug: string;
  readonly fxRate: number;
  readonly isInput: boolean;
  readonly padClass: string;
}): JSX.Element {
  const entry = model.channels.find((c) => c.slug === channelSlug);
  if (!entry) return <td className={`${padClass} text-center text-ink-subtle/30`}>—</td>;
  const usd = isInput ? model.input_per_mtok_usd! : model.output_per_mtok_usd!;
  return <td className={`${padClass} text-right font-mono text-ink`}>{fmtCNY(usd, entry.rate_multiplier, fxRate)}</td>;
}

interface Props {
  readonly catalogModels: readonly CatalogModel[] | null;
  readonly catalogVendors: readonly CatalogVendor[] | null;
  readonly fxRate: number;
}

export function Pricing({ catalogModels, catalogVendors, fxRate }: Props): JSX.Element {
  const t = useT();
  const vendors = catalogVendors ?? [];
  const { groups, maxChannels } = useMemo(() => buildData(catalogModels ?? []), [catalogModels]);
  const totalCols = 3 + maxChannels;

  return (
    <section id="pricing" className="bg-butter/40 py-28" data-testid="pricing">
      <div className="mx-auto max-w-7xl px-6">
        <div className="mb-14 max-w-3xl">
          <p className="mb-4 font-mono text-[11px] font-medium uppercase tracking-[0.22em] text-claude">
            {t('pricing.eyebrow')}
          </p>
          <h2 className="text-[40px] font-semibold leading-[1.05] text-ink md:text-[52px]">
            <span className="serif text-claude">{t('pricing.title.accent')}</span>
            {t('pricing.title.trail')}
          </h2>
          <p className="mt-5 text-[16px] text-ink-muted">{t('pricing.subtitle')}</p>
        </div>

        <div className="ghost overflow-hidden rounded-3xl bg-linen">
          {groups.length === 0 ? (
            <div className="h-40 animate-pulse rounded-3xl bg-butter/50" />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left table-fixed">
                <colgroup>
                  <col className="w-[180px]" />
                  <col className="w-[90px]" />
                  <col className="w-[100px]" />
                  {Array.from({ length: maxChannels }, (_, i) => (
                    <col key={i} />
                  ))}
                </colgroup>
                <tbody className="text-[13px]">
                  {groups.map((group) => {
                    const padCount = maxChannels - group.channels.length;
                    return [
                      <tr key={`hdr-${group.vendor}`} className="bg-butter/40">
                        <td colSpan={totalCols} className="px-6 py-2.5 text-[13px] font-semibold text-ink">
                          {vendorName(group.vendor, vendors)}
                        </td>
                      </tr>,
                      <tr key={`cols-${group.vendor}`} className="font-mono text-[11px] uppercase tracking-[0.16em] text-ink-subtle">
                        <th className="px-6 py-3 font-medium">{t('pricing.col.model')}</th>
                        <th className="px-2 py-3 font-medium" />
                        <th className="px-5 py-3 text-right font-medium">{t('pricing.col.official')}</th>
                        {group.channels.map((ch) => (
                          <th key={ch.slug} className="px-5 py-3 text-right font-medium">
                            <div>{ch.display_name}</div>
                            <div className="text-[10px] font-semibold text-claude">{fmtDiscount(ch.rate_multiplier)}</div>
                          </th>
                        ))}
                        {padCount > 0 && <th colSpan={padCount} />}
                      </tr>,
                      ...group.models.flatMap((m) => {
                        const chMap = new Map(m.channels.map((c) => [c.slug, c]));
                        return [
                          <tr key={`${m.model}-in`} className="ghost-t">
                            <td className="px-6 pt-3 pb-0.5 font-medium text-ink" rowSpan={2}>
                              {m.display_name ?? m.model}
                            </td>
                            <td className="px-2 pt-3 pb-0.5 text-[11px] text-ink-subtle whitespace-nowrap">{t('pricing.row.input')}</td>
                            <td className="px-5 pt-3 pb-0.5 text-right font-mono text-ink-subtle">
                              {fmtCNY(m.input_per_mtok_usd!, 1, fxRate)}
                            </td>
                            {group.channels.map((ch) => (
                              <ChannelCell key={ch.slug} model={m} channelSlug={ch.slug} fxRate={fxRate} isInput={true} padClass="px-5 pt-3 pb-0.5" />
                            ))}
                            {padCount > 0 && <td colSpan={padCount} />}
                          </tr>,
                          <tr key={`${m.model}-out`}>
                            <td className="px-2 pt-0.5 pb-3 text-[11px] text-ink-subtle whitespace-nowrap">{t('pricing.row.output')}</td>
                            <td className="px-5 pt-0.5 pb-3 text-right font-mono text-ink-subtle">
                              {fmtCNY(m.output_per_mtok_usd!, 1, fxRate)}
                            </td>
                            {group.channels.map((ch) => (
                              <ChannelCell key={ch.slug} model={m} channelSlug={ch.slug} fxRate={fxRate} isInput={false} padClass="px-5 pt-0.5 pb-3" />
                            ))}
                            {padCount > 0 && <td colSpan={padCount} />}
                          </tr>,
                        ];
                      }),
                    ];
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="mt-5 font-mono text-[12.5px] text-ink-subtle">
          {t('pricing.footnote')}
        </p>
      </div>
    </section>
  );
}
